"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.lazyWithRetry = void 0;
const react_1 = __importDefault(require("react"));
/** So the reload can only ever happen once per tab, however many chunks fail. */
const RELOAD_FLAG = 'ds-chunk-reload';
const hasReloaded = () => {
    try {
        return window.sessionStorage.getItem(RELOAD_FLAG) === 'true';
    }
    catch {
        // storage is unavailable in some privacy modes — better to skip the reload than
        // to throw a second error on top of the one being handled
        return true;
    }
};
const rememberReload = () => {
    try {
        window.sessionStorage.setItem(RELOAD_FLAG, 'true');
    }
    catch { }
};
const forgetReload = () => {
    try {
        window.sessionStorage.removeItem(RELOAD_FLAG);
    }
    catch { }
};
/**
 * `React.lazy` that survives a release.
 *
 * Chunk file names carry a content hash, so a tab left open across a deploy holds a
 * bundle whose chunk names the server no longer has — and ours answers an unknown path
 * with index.html rather than a 404, so the browser gets HTML where it asked for a
 * script and webpack raises `ChunkLoadError`. The build in memory cannot recover from
 * that; only a reload can, which is what this does. It is what makes logging out throw:
 * `/login` is lazy and a signed-in session never loads it, so it is usually the first
 * brand-new chunk a long-lived tab asks for.
 *
 * The reload is taken once per tab, so a chunk that fails for any other reason still
 * ends up at the error boundary instead of in a reload loop.
 */
const lazyWithRetry = (factory) => react_1.default.lazy(async () => {
    try {
        const component = await factory();
        forgetReload();
        return component;
    }
    catch (error) {
        if (hasReloaded())
            throw error;
        rememberReload();
        window.location.reload();
        // reloading does not stop the current script — hang Suspense on the way out
        // rather than let the error boundary flash up first
        return await new Promise(() => { });
    }
});
exports.lazyWithRetry = lazyWithRetry;
exports.default = exports.lazyWithRetry;
